import { useRef, useState } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload, Download, FileSpreadsheet, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";

/**
 * XLSX template download + import dialog.
 * entity: "revenue-details" | "engagements"
 */
export default function ImportDialog({ entity, entityLabel, onImported, testIdPrefix }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null); // {added, skipped, errors}
  const inputRef = useRef(null);

  const downloadTemplate = async () => {
    try {
      const res = await api.get(`/${entity}/template`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const fileNameBase = String(entityLabel || entity)
        .replace(/[^A-Za-z0-9]+/g, "_")
        .replace(/^_|_$/g, "");
      a.download = `${fileNameBase || entity}_Template.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Template downloaded");
    } catch {
      toast.error("Failed to download template");
    }
  };

  const upload = async () => {
    if (!file) {
      toast.error("Choose an .xlsx file first.");
      return;
    }
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post(`/${entity}/import`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(res.data);
      if (res.data.added > 0) {
        toast.success(`${res.data.added} row(s) imported into ${entityLabel}`);
        onImported?.();
      } else {
        toast.warning("No rows were imported. Check the report below.");
      }
    } catch (e) {
      toast.error(apiErrorMessage(e, "Import failed"));
    } finally {
      setUploading(false);
    }
  };

  const reset = () => {
    setFile(null);
    setResult(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <>
      <Button variant="outline" size="sm" className="rounded-lg" data-testid={`${testIdPrefix}-import-button`}
        onClick={() => { reset(); setOpen(true); }}>
        <Upload className="h-3.5 w-3.5 mr-1.5" /> Import
      </Button>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Import {entityLabel} from Excel</DialogTitle>
            <DialogDescription>
              Download the template, fill one row per line, then upload it. Valid rows are appended;
              rows with errors are skipped and reported.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="rounded-lg border p-3 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5 min-w-0">
                <FileSpreadsheet className="h-5 w-5 text-teal-600 shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium">Step 1 - Get the template</p>
                  <p className="text-xs text-muted-foreground">Includes an Instructions sheet</p>
                </div>
              </div>
              <Button variant="outline" size="sm" className="rounded-lg shrink-0"
                data-testid={`${testIdPrefix}-download-template`} onClick={downloadTemplate}>
                <Download className="h-3.5 w-3.5 mr-1.5" /> Template
              </Button>
            </div>

            <div className="rounded-lg border p-3 space-y-2">
              <p className="text-sm font-medium">Step 2 - Upload your filled file (.xlsx)</p>
              <input ref={inputRef} type="file" accept=".xlsx" data-testid={`${testIdPrefix}-file-input`}
                onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
                className="block w-full text-xs text-muted-foreground file:mr-3 file:rounded-lg file:border file:border-input file:bg-background file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-foreground hover:file:bg-accent file:cursor-pointer cursor-pointer" />
            </div>

            {result && (
              <div className="rounded-lg border p-3 space-y-2" data-testid={`${testIdPrefix}-import-result`}>
                <div className="flex items-center gap-4 text-sm">
                  <span className="inline-flex items-center gap-1.5 text-emerald-700 dark:text-emerald-400 font-medium">
                    <CheckCircle2 className="h-4 w-4" /> {result.added} imported
                  </span>
                  {result.skipped > 0 && (
                    <span className="inline-flex items-center gap-1.5 text-amber-700 dark:text-amber-400 font-medium">
                      <AlertTriangle className="h-4 w-4" /> {result.skipped} row(s) skipped
                    </span>
                  )}
                </div>
                {result.errors?.length > 0 && (
                  <div className="max-h-40 overflow-y-auto rounded border bg-amber-50/50 dark:bg-amber-950/20">
                    <table className="w-full text-[11px]">
                      <thead>
                        <tr className="text-left text-muted-foreground border-b">
                          <th className="px-2 py-1 font-medium">Row</th>
                          <th className="px-2 py-1 font-medium">Field</th>
                          <th className="px-2 py-1 font-medium">Problem</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.errors.map((e, i) => (
                          <tr key={i} className="border-b last:border-0">
                            <td className="px-2 py-1 tabular-nums">{e.row}</td>
                            <td className="px-2 py-1">{e.field}</td>
                            <td className="px-2 py-1">{e.message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
            <Button data-testid={`${testIdPrefix}-upload-submit`} onClick={upload} disabled={uploading || !file}>
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-3.5 w-3.5 mr-1.5" /> Upload & Import</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
